import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import type { InterfacePageEmbeddedRequest } from '@unifyapps/network/generated/models/interfacePageEmbeddedRequest';
import type { InterfacePageEmbeddedRequestExcludeFields } from '@unifyapps/network/generated/models/interfacePageEmbeddedRequestExcludeFields';
import InterfaceHelper from '../../stores/InterfaceStore/helper/InterfaceHelper';
import type { InterfaceComponent } from '../../types/interfaceComponent';

interface BuildArgs {
  interfaceId: string;
  interfaceComponent: InterfaceComponent;
  interfaceRecord: InterfaceEntity | undefined;
  defaultPageId?: string;
  excludeFields?: InterfacePageEmbeddedRequestExcludeFields;
  allowedEntityTypes?: string[];
}

interface BuildResult {
  request: InterfacePageEmbeddedRequest;
  enabled: boolean;
  // null when we're going through the slug lookup (id will only be known after the response).
  resolvedPageId: string | null;
}

const EMPTY_REQUEST: InterfacePageEmbeddedRequest = {
  entityId: '',
};

/**
 * Builds the InterfacePageEmbeddedRequest for fetching a page + its dependents.
 *
 * Resolution order: slug → id via interface record's slug map, slug-as-id (legacy links),
 * defaultPageId. When all three fail AND the interface record has loaded but the slug
 * isn't in its map, send `interfaceId` + `slug` and let the backend resolve the page.
 * Keeps backward compat for stale slug maps and legacy links without rolling back the
 * embedded-dependents prefetch optimization.
 *
 * The endpoint is e_component-specific (`/embedded-entities/e_component`), so entityType
 * is no longer part of the body. Every field here is typed — the frontend never sends a
 * filter, which is the whole point of the dedicated endpoint.
 */
export function buildInterfacePageEmbeddedRequest({
  interfaceId,
  interfaceComponent,
  interfaceRecord,
  defaultPageId,
  excludeFields,
  allowedEntityTypes,
}: BuildArgs): BuildResult {
  const resolvedPageId =
    InterfaceHelper.getInterfaceComponentIdFromSlug(interfaceRecord, interfaceComponent) ??
    InterfaceHelper.getInterfaceComponentIdIfSlugIsId(interfaceRecord, interfaceComponent) ??
    defaultPageId;

  if (resolvedPageId) {
    return {
      request: {
        entityId: resolvedPageId,
        ...(excludeFields ? { excludeFields } : {}),
        ...(allowedEntityTypes ? { allowedEntityTypes } : {}),
      },
      enabled: true,
      resolvedPageId,
    };
  }

  const isPageWithSlug =
    interfaceComponent.type === InterfacePageEntityComponentType.PAGE &&
    Boolean(interfaceComponent.slug);

  // Only fire the slug lookup when the interface record has actually loaded - while it's
  // still in flight, slug → id resolution might succeed once it arrives, so doing both is
  // wasteful.
  if (isPageWithSlug && interfaceId && interfaceRecord) {
    // interfaceId + slug is the backend's cue to resolve the page itself. It matches on
    // `properties.slug == slug OR id == slug` — the id arm is what keeps pages saved before
    // the slug field existed reachable.
    return {
      request: {
        interfaceId,
        slug: interfaceComponent.slug,
        ...(excludeFields ? { excludeFields } : {}),
        ...(allowedEntityTypes ? { allowedEntityTypes } : {}),
      },
      enabled: true,
      resolvedPageId: null,
    };
  }

  return { request: EMPTY_REQUEST, enabled: false, resolvedPageId: null };
}
